/**
 * Waiting out the crop, which every artwork upload in this console has to do.
 *
 * `POST /artworks/{id}:completeUpload` is the commit point and **not** the point
 * the image exists: it returns as soon as the crop job is queued, and the
 * `crop` role drains that table on its own tick. `GET /artworks` answers with
 * `READY` derived rows only, so a read taken the moment the upload resolves
 * comes back empty almost every time — and a surface that invalidated its query
 * there drew an empty box and told the operator to reload the page.
 *
 * So every upload site polls instead, and this is the one loop they share. It
 * is **bounded**: an operator is standing in front of the control, and a
 * derivation that has not landed inside the window is one the caller reports
 * rather than spins on forever — `assets` marks a job `FAILED` without anything
 * telling this console, so an open-ended wait would be a permanent spinner.
 */

/** Six attempts a second and a half apart — about nine seconds, then give up. */
export const DERIVATION_POLL_ATTEMPTS = 6;
export const DERIVATION_POLL_INTERVAL_MS = 1_500;

/**
 * Read until the derivation shows up, or until the window closes.
 *
 * Returns whatever the last read produced, so a caller that timed out can still
 * state what it saw rather than throwing away the answer. `isPresent` is the
 * caller's own test — a URL becoming non-null, a row appearing, a column being
 * filled by the owning service's write-back — because what «derived» means
 * differs per subject and only the caller knows which surface it is drawing.
 */
export async function waitForDerivation<T>(
  read: () => Promise<T>,
  isPresent: (value: T) => boolean,
): Promise<T> {
  let latest = await read();

  for (let attempt = 1; attempt < DERIVATION_POLL_ATTEMPTS; attempt += 1) {
    if (isPresent(latest)) return latest;
    await new Promise((resolve) => setTimeout(resolve, DERIVATION_POLL_INTERVAL_MS));
    latest = await read();
  }

  return latest;
}
